import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Plus, AlertTriangle, Loader2 } from "lucide-react";

const TONE = { High: "bg-red-50 text-red-700 border-red-200", Moderate: "bg-amber-50 text-amber-700 border-amber-200", Low: "bg-slate-50 text-slate-600 border-slate-200" };
const append = (cur, add) => (cur ? `${cur.trim()}\n${add}` : add);

export function AIAssistCard({ opdId, form, set }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/opd/${opdId}/ai-assist`, {
        chief_complaint: form.chief_complaint || "", clinical_findings: form.clinical_findings || "",
        diagnosis: form.diagnosis || "", treatment: form.treatment || "", vitals: form.vitals || {},
        medicines: (form.medicines || []).map((m) => m.name).filter(Boolean),
      });
      setRes(data);
    } catch (e) { toast.error(e.response?.data?.detail || "AI request failed"); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-violet-200 p-4 space-y-3" data-testid="ai-assist-card">
      <div className="flex items-center justify-between">
        <div className="label-tiny flex items-center gap-1 text-violet-800"><Sparkles className="w-3.5 h-3.5" /> AI Assistant</div>
        <Button size="sm" onClick={run} disabled={busy} className="h-8 rounded-sm bg-violet-700 hover:bg-violet-800" data-testid="ai-assist-run">
          {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}{res ? "Re-analyse" : "Suggest"}
        </Button>
      </div>
      {!res && !busy && <p className="text-[11px] text-slate-500">Fill complaints, findings and vitals, then tap Suggest for possible diagnoses, red flags, tests and advice.</p>}
      {res && <AIResult res={res} form={form} set={set} />}
    </div>
  );
}

function AIResult({ res, form, set }) {
  return (
    <div className="space-y-3 text-xs" data-testid="ai-assist-result">
      {res.summary && <p className="text-slate-700">{res.summary}</p>}
      {res.red_flags.length > 0 && (
        <div className="border border-red-200 bg-red-50 p-2 space-y-0.5" data-testid="ai-red-flags">
          <div className="font-semibold text-red-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Red flags</div>
          {res.red_flags.map((r, i) => <div key={i} className="text-red-800">• {r}</div>)}
        </div>
      )}
      <Section title="Possible diagnoses">
        {res.differentials.map((d, i) => (
          <div key={i} className="flex items-start justify-between gap-2 border-b last:border-0 py-1" data-testid={`ai-dx-${i}`}>
            <div>
              <div className="font-medium">{d.diagnosis} <span className={`ml-1 text-[10px] border rounded-sm px-1 ${TONE[d.likelihood] || TONE.Low}`}>{d.likelihood}</span></div>
              <div className="text-slate-500">{d.reasoning}</div>
            </div>
            <UseBtn testid={`ai-dx-use-${i}`} onClick={() => set("diagnosis", append(form.diagnosis, d.diagnosis))} />
          </div>
        ))}
      </Section>
      {res.suggested_investigations.length > 0 && (
        <Section title="Suggested investigations">
          <div className="flex flex-wrap gap-1">{res.suggested_investigations.map((t) => <span key={t} className="border border-slate-300 rounded-sm px-1.5 py-0.5">{t}</span>)}</div>
        </Section>
      )}
      {res.advice.length > 0 && (
        <Section title="Advice">
          {res.advice.map((a, i) => (
            <div key={i} className="flex items-start justify-between gap-2 py-0.5">
              <span>• {a}</span>
              <UseBtn testid={`ai-advice-use-${i}`} onClick={() => set("advice", append(form.advice, a))} />
            </div>
          ))}
        </Section>
      )}
      <p className="text-[10px] text-slate-400 italic">AI suggestions are for support only — clinical judgement of the treating doctor prevails.</p>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{title}</div>{children}</div>
);
const UseBtn = ({ onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid} className="shrink-0 text-violet-700 hover:text-violet-900 flex items-center gap-0.5"><Plus className="w-3 h-3" />Use</button>
);
